import * as THREE from 'three';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import {
  createRangerPup,
  type CharacterExpression as RigExpression,
  type CharacterGesture,
  type CharacterMotion,
  type RangerPupCharacter,
  type RangerPupId,
} from '../characters';
import { Loop } from '../core/Loop';
import { createRenderer, resizeRenderer } from '../core/Renderer';
import { getScene, getShot, WHERE_THE_LAKE_ENDS, type CameraCue, type CharacterAction, type CharacterExpression } from '../story/episode';
import { StudioController } from '../studio';
import type { DirectionSettings, StudioCommand } from '../studio/types';
import { AudioSystem } from '../systems/AudioSystem';
import { CaptureSystem, type CaptureMetadata } from '../systems/CaptureSystem';
import { CinematicCamera } from '../systems/CinematicCamera';
import { StudioUI } from '../ui';
import { createLakesideVillage, createVillageLightingRig } from '../world';

const STORY_TO_RIG: Record<string, RangerPupId> = {
  arlo: 'arlo', pasha: 'pasha', liam: 'liam', milo: 'milo', zara: 'zara', tuk: 'tuk', mom: 'mara', dad: 'rowan',
};

const CAST_IDS: RangerPupId[] = ['arlo', 'pasha', 'liam', 'milo', 'zara', 'tuk', 'mara', 'rowan', 'ember'];

const ACTION_RIG: Record<CharacterAction, { motion: CharacterMotion; gesture: CharacterGesture; speed: number }> = {
  idle: { motion: 'idle', gesture: 'none', speed: 0 },
  'look-out': { motion: 'idle', gesture: 'point', speed: 0 },
  run: { motion: 'run', gesture: 'none', speed: 1.1 },
  investigate: { motion: 'sneak', gesture: 'listen', speed: 0.25 },
  repair: { motion: 'idle', gesture: 'point', speed: 0 },
  comfort: { motion: 'idle', gesture: 'listen', speed: 0 },
  'group-promise': { motion: 'idle', gesture: 'cheer', speed: 0 },
};

const EXPRESSION_RIG: Record<CharacterExpression, RigExpression> = {
  curious: 'curious', hopeful: 'determined', joy: 'happy', alert: 'concerned', focused: 'determined', kind: 'happy', wonder: 'curious',
};

const FOG: Record<DirectionSettings['timeOfDay'], string> = {
  morning: '#c9d6c8', golden: '#d5b58d', forest: '#6f8d7d', 'blue-hour': '#5c6881',
};

export class Game {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(32, 1, 0.1, 220);
  private readonly directorCamera = new CinematicCamera(this.camera);
  private readonly studio = new StudioController();
  private readonly studioUI = new StudioUI(this.studio);
  private readonly capture: CaptureSystem;
  private readonly audio = new AudioSystem();
  private readonly world = createLakesideVillage();
  private readonly lighting = createVillageLightingRig();
  private readonly castRoot = new THREE.Group();
  private readonly cast = new Map<RangerPupId, RangerPupCharacter>();
  private readonly removeStudioListener: () => void;
  private readonly loop = new Loop((delta, elapsed) => this.update(delta, elapsed), () => this.render());
  private frame = 0;
  private elapsed = 0;
  private reducedMotion = false;
  private pausedForScreenshot = false;
  private lastAppliedShotId = '';

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.renderer = createRenderer(canvas);
    this.capture = new CaptureSystem(canvas);
    this.scene.fog = new THREE.FogExp2(FOG.morning, 0.0125);
    this.scene.add(this.world.root, this.lighting.root, this.castRoot);
    this.createCast();
    this.createEnvironment();
    this.removeStudioListener = this.studio.on('studio:command', (event) => this.onStudioCommand(event.detail));
    this.applyCurrentShot(true);
    resizeRenderer(this.renderer, this.camera, 2);
    this.installTestHooks();
    this.publishDiagnostics();
  }

  start(): void { this.loop.start(); }

  dispose(): void {
    this.loop.stop();
    this.removeStudioListener();
    this.studioUI.dispose();
    this.audio.dispose();
    if (this.capture.isRecording) this.capture.stopRecording();
    for (const character of this.cast.values()) character.dispose();
    this.world.dispose();
    this.scene.environment?.dispose();
    this.renderer.dispose();
    window.__THREE_GAME_DIAGNOSTICS__ = undefined;
    window.__THREE_GAME_TEST_HOOKS__ = undefined;
  }

  private createEnvironment(): void {
    const environment = new RoomEnvironment();
    const generator = new THREE.PMREMGenerator(this.renderer);
    this.scene.environment = generator.fromScene(environment, 0.04).texture;
    environment.dispose();
    generator.dispose();
  }

  private createCast(): void {
    this.castRoot.name = 'episodeCast';
    for (const id of CAST_IDS) {
      const character = createRangerPup(id);
      character.root.visible = false;
      this.cast.set(id, character);
      this.castRoot.add(character.root);
    }
  }

  private update(delta: number, elapsed: number): void {
    this.frame += 1;
    if (this.pausedForScreenshot) { this.publishDiagnostics(); return; }
    this.elapsed = elapsed;
    resizeRenderer(this.renderer, this.camera, 2);
    this.studio.tick(delta);
    if (this.studio.shot.id !== this.lastAppliedShotId) this.applyCurrentShot(false);
    const animationDelta = this.reducedMotion ? 0 : delta;
    const animationTime = this.reducedMotion ? 1.25 : this.studio.state.currentTime + elapsed * 0.05;
    this.world.update(animationDelta, animationTime);
    for (const character of this.cast.values()) if (character.root.visible) character.update(animationDelta, animationTime);
    this.directorCamera.update(delta, this.studio.state.currentTime, this.reducedMotion);
    this.publishDiagnostics();
  }

  private render(): void { this.renderer.render(this.scene, this.camera); }

  private onStudioCommand(command: StudioCommand): void {
    if (command.type === 'scene-selected' || command.type === 'shot-selected') {
      this.audio.cue('cut');
      this.applyCurrentShot(false);
      return;
    }
    if (command.type === 'seek') {
      if (this.studio.shot.id !== this.lastAppliedShotId) this.applyCurrentShot(false);
      return;
    }
    if (command.type === 'playback') {
      this.audio.setAmbience(command.playing);
      this.showToast(command.playing ? 'Playing scene' : 'Scene paused');
      return;
    }
    if (command.type === 'direction-changed') { this.applyDirection(command.direction); return; }
    if (command.type === 'asset-selected') {
      this.audio.cue('select');
      this.showToast(`${this.studio.assets.find((asset) => asset.id === command.assetId)?.name ?? 'Asset'} selected`);
      return;
    }
    if (command.type === 'capture-frame') {
      this.render();
      void this.capture.captureStill(this.captureMetadata()).then(() => {
        this.audio.cue('capture');
        this.showToast('PNG frame captured');
      }).catch(() => this.showToast('Capture unavailable'));
      return;
    }
    if (command.type === 'export-scene') this.exportScene(command);
  }

  private exportScene(command: Extract<StudioCommand, { type: 'export-scene' }>): void {
    if (command.settings.format === 'Direction JSON') {
      this.capture.exportDirection(this.captureMetadata(), WHERE_THE_LAKE_ENDS);
      this.showToast('Direction package exported');
      return;
    }
    if (this.capture.isRecording) {
      this.capture.stopRecording();
      this.audio.cue('record-stop');
      this.studio.pause();
      this.showToast('WebM take saved');
      return;
    }
    if (this.capture.startRecording()) {
      this.audio.cue('record-start');
      this.studio.play();
      this.showToast('Recording WebM take');
    } else this.showToast('Recording is not supported in this browser');
  }

  private applyCurrentShot(immediate: boolean): void {
    const storyScene = getScene(this.studio.scene.id);
    const storyShot = getShot(this.studio.scene.id, this.studio.shot.id);
    if (!storyScene || !storyShot) return;
    const anchor = this.world.anchors[storyScene.id] ?? new THREE.Vector3();
    this.lastAppliedShotId = storyShot.id;
    this.castRoot.position.copy(anchor);
    for (const character of this.cast.values()) {
      character.root.visible = false;
      character.root.scale.setScalar(1);
      character.visual.rotation.set(0, 0, 0);
    }
    for (const cue of storyShot.direction) {
      const character = this.cast.get(STORY_TO_RIG[cue.character]);
      if (!character) continue;
      character.root.visible = true;
      character.root.position.fromArray(cue.mark);
      character.root.rotation.set(0, cue.facing + Math.PI, 0);
      character.resetAnimationPhase(0);
      this.setPerformance(character, cue.action, cue.expression);
      character.setLookTarget(new THREE.Vector3().copy(anchor).add(new THREE.Vector3().fromArray(storyShot.camera.target)));
    }
    if (storyScene.id === 'forest-dog') {
      const forestDog = this.cast.get('ember');
      if (forestDog) {
        forestDog.root.visible = true;
        forestDog.root.position.set(0.25, 0.18, -1.45);
        forestDog.root.rotation.set(0, Math.PI, 0);
        forestDog.visual.rotation.z = -0.42;
        forestDog.resetAnimationPhase(0);
        forestDog.setAnimation({ motion: 'idle', gesture: 'none', expression: 'concerned', speed: 0 });
      }
    }
    this.world.setTimeOfDay(storyScene.timeOfDay);
    this.lighting.setTimeOfDay(storyScene.timeOfDay);
    this.updateFog(storyScene.timeOfDay);
    this.directorCamera.setCue(this.offsetCamera(storyShot.camera, anchor), immediate);
  }

  private applyDirection(direction: DirectionSettings): void {
    const storyShot = getShot(this.studio.scene.id, this.studio.shot.id);
    const anchor = this.world.anchors[this.studio.scene.id] ?? new THREE.Vector3();
    if (storyShot) {
      const cameraCue = this.offsetCamera({ ...storyShot.camera, preset: direction.cameraPreset }, anchor);
      cameraCue.fov = this.framingFov(this.lensToFov(direction.lensMm));
      this.directorCamera.setCue(cameraCue, false);
    }
    this.world.setTimeOfDay(direction.timeOfDay);
    this.lighting.setTimeOfDay(direction.timeOfDay);
    this.updateFog(direction.timeOfDay);
    const lead = [...this.cast.values()].find((character) => character.root.visible);
    if (lead) this.setPerformance(lead, direction.action, direction.expression);
    this.audio.cue('select');
  }

  private setPerformance(character: RangerPupCharacter, action: CharacterAction, expression: CharacterExpression): void {
    character.setAnimation({ ...ACTION_RIG[action], expression: EXPRESSION_RIG[expression] });
  }

  private offsetCamera(cue: CameraCue, anchor: THREE.Vector3): CameraCue {
    const position = new THREE.Vector3().fromArray(cue.position).add(anchor);
    const target = new THREE.Vector3().fromArray(cue.target).add(anchor);
    return { ...cue, fov: this.framingFov(cue.fov), position: position.toArray(), target: target.toArray() };
  }

  private updateFog(time: DirectionSettings['timeOfDay']): void {
    if (this.scene.fog instanceof THREE.FogExp2) {
      this.scene.fog.color.set(FOG[time]);
      this.scene.fog.density = time === 'forest' ? 0.017 : time === 'blue-hour' ? 0.014 : 0.0115;
    }
  }

  private lensToFov(lensMm: number): number { return THREE.MathUtils.radToDeg(2 * Math.atan(18 / Math.max(18, lensMm))); }

  private framingFov(fov: number): number {
    const aspect = this.canvas.clientWidth / Math.max(1, this.canvas.clientHeight);
    return aspect < 1.3 ? fov * 1.12 : fov;
  }

  private captureMetadata(): CaptureMetadata {
    return {
      episodeId: WHERE_THE_LAKE_ENDS.id,
      sceneId: this.studio.scene.id,
      shotId: this.studio.shot.id,
      time: this.studio.state.currentTime,
      camera: { position: this.camera.position.toArray(), target: this.directorCamera.getTarget().toArray(), fov: this.camera.fov },
    };
  }

  private showToast(message: string): void {
    let toast = document.querySelector<HTMLElement>('#studio-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'studio-toast';
      toast.className = 'studio-toast';
      toast.setAttribute('role', 'status');
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.classList.remove('is-visible');
    requestAnimationFrame(() => toast?.classList.add('is-visible'));
    window.setTimeout(() => toast?.classList.remove('is-visible'), 2100);
  }

  private installTestHooks(): void {
    window.__THREE_GAME_TEST_HOOKS__ = {
      seed: () => undefined,
      setState: (name: string) => {
        const sceneId = name === 'active-play' ? 'mirror' : name;
        if (getScene(sceneId)) this.studio.selectScene(sceneId);
        else console.warn(`Unknown test state: ${name}`);
      },
      setPausedForScreenshot: (paused: boolean) => { this.pausedForScreenshot = paused; },
      setReducedMotion: (enabled: boolean) => { this.reducedMotion = enabled; },
      hideDebugUi: () => undefined,
    };
  }

  private publishDiagnostics(): void {
    const info = this.renderer.info;
    window.__THREE_GAME_DIAGNOSTICS__ = {
      frame: this.frame, elapsed: this.elapsed, state: 'studio', sceneId: this.studio.scene.id, shotId: this.studio.shot.id,
      currentTime: this.studio.state.currentTime, isPlaying: this.studio.state.isPlaying,
      visibleCast: [...this.cast.entries()].filter(([, character]) => character.root.visible).map(([id]) => id),
      renderer: { calls: info.render.calls, triangles: info.render.triangles, geometries: info.memory.geometries, textures: info.memory.textures },
      world: this.world.diagnostics,
      canvas: { clientWidth: this.canvas.clientWidth, clientHeight: this.canvas.clientHeight, width: this.canvas.width, height: this.canvas.height, dpr: Math.min(window.devicePixelRatio || 1, 2) },
    };
  }
}
